import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Thème d'affichage : clair, sombre, ou celui de l'appareil.
 *
 * Le réglage vit à deux endroits, et ce n'est pas une redondance :
 *
 * - dans le compte, pour qu'il suive d'un appareil à l'autre ;
 * - dans le navigateur, pour être connu *avant* que le serveur ait répondu.
 *   Sans cette copie locale, chaque chargement afficherait une page blanche
 *   le temps de l'aller-retour, puis basculerait au noir sous les yeux de
 *   l'utilisateur.
 *
 * La copie locale est donc l'autorité au démarrage, le compte prend le relais
 * dès qu'il est connu.
 */

const CLE = 'holidai.theme';

export const THEMES = [
  ['system', 'Selon l’appareil'],
  ['light', 'Clair'],
  ['dark', 'Sombre'],
];

const CONNUS = THEMES.map(([code]) => code);

/** Couleur de la barre d'adresse sur mobile, pour chaque thème résolu. */
const COULEUR_BARRE = { light: '#eef3f7', dark: '#10151c' };

const requeteSombre = () =>
  (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null);

export function lireThemeLocal() {
  try {
    const valeur = localStorage.getItem(CLE);
    return CONNUS.includes(valeur) ? valeur : 'system';
  } catch {
    // Navigation privée, stockage refusé : le réglage par défaut fera l'affaire.
    return 'system';
  }
}

function memoriser(theme) {
  try {
    localStorage.setItem(CLE, theme);
  } catch {
    // Sans stockage, le thème vaut pour la session en cours. Ce n'est pas une
    // raison de faire échouer le changement.
  }
}

/** Applique le thème au document et renvoie celui qui a été retenu. */
export function appliquerTheme(theme) {
  const resolu = theme === 'system' ? (requeteSombre()?.matches ? 'dark' : 'light') : theme;

  document.documentElement.dataset.theme = resolu;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', COULEUR_BARRE[resolu]);

  return resolu;
}

const ContexteTheme = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(lireThemeLocal);

  useEffect(() => {
    appliquerTheme(theme);
    if (theme !== 'system') return undefined;

    // En mode « selon l'appareil », le réglage du système peut changer pendant
    // que la page est ouverte — au coucher du soleil, sur un téléphone réglé
    // en automatique. On suit le changement plutôt que d'attendre un
    // rechargement.
    const requete = requeteSombre();
    const reagir = () => appliquerTheme('system');
    requete?.addEventListener('change', reagir);

    return () => requete?.removeEventListener('change', reagir);
  }, [theme]);

  const valeur = useMemo(
    () => ({
      theme,
      /**
       * Change le thème immédiatement. La persistance dans le compte est
       * l'affaire de l'appelant : l'affichage ne doit pas attendre le réseau
       * pour réagir à un clic, et une requête qui échoue ne doit pas laisser
       * l'écran dans un état que personne n'a choisi.
       */
      choisirTheme: (choisi) => {
        if (!CONNUS.includes(choisi)) return;
        memoriser(choisi);
        setTheme(choisi);
      },
    }),
    [theme],
  );

  return <ContexteTheme.Provider value={valeur}>{children}</ContexteTheme.Provider>;
}

export function useTheme() {
  const contexte = useContext(ContexteTheme);
  if (!contexte) throw new Error('useTheme doit être utilisé dans un ThemeProvider.');
  return contexte;
}
